import { useQuery } from '@tanstack/react-query'
import {
    getCommonScoresApiV1StatsOrgIdCommonGet,
    generationScoreStatsApiV1StatsOrgIdScoresGet,
    generationSegmentScoreStatsApiV1StatsOrgIdSegmentScoresGet,
    getTopBottomProductsApiV1StatsOrgIdProductsGet,
    getProductsScoresApiV1StatsOrgIdProductsScoresGet,
} from '@/common/api/client/sdk.gen'
import type {
    GetCommonScoresApiV1StatsOrgIdCommonGetResponse,
    GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse,
    GenerationSegmentScoreStatsApiV1StatsOrgIdSegmentScoresGetResponse,
    GetTopBottomProductsApiV1StatsOrgIdProductsGetResponse,
    GetProductsScoresApiV1StatsOrgIdProductsScoresGetResponse,
} from '@/common/api/client/types.gen'
import type { AxiosError } from 'axios'

export type CompanyStatsQueryFilters = {
    brand?: string
    seller?: string
}

export type CompanyStatsProductsScoresOrderBy = NonNullable<
    NonNullable<Parameters<typeof getProductsScoresApiV1StatsOrgIdProductsScoresGet>[0]>['query']
>['order_by']

export type CompanyStatsProductsScoresTarget = NonNullable<
    NonNullable<Parameters<typeof getProductsScoresApiV1StatsOrgIdProductsScoresGet>[0]>['query']
>['target']

export type CompanyStatsProductsScoresQueryParams = {
    page: number
    size: number
    order_by?: CompanyStatsProductsScoresOrderBy
    direction?: 'asc' | 'desc'
    target?: CompanyStatsProductsScoresTarget
    brand?: string
    seller?: string
}

export const useCompanyStatsCommon = (orgId: string | undefined, filters?: CompanyStatsQueryFilters, enabled = true) =>
    useQuery<GetCommonScoresApiV1StatsOrgIdCommonGetResponse, AxiosError>({
        queryKey: ['company-stats', 'common', orgId, filters],
        queryFn: async () => {
            const res = await getCommonScoresApiV1StatsOrgIdCommonGet({
                path: { org_id: orgId! },
                query: { brand: filters?.brand ?? null, seller: filters?.seller ?? null },
            })
            return res.data as GetCommonScoresApiV1StatsOrgIdCommonGetResponse
        },
        retry: false,
        enabled: enabled && Boolean(orgId),
        refetchOnWindowFocus: false,
    })

export const useCompanyStatsScores = (orgId: string | undefined, filters?: CompanyStatsQueryFilters, enabled = true) =>
    useQuery<GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse, AxiosError>({
        queryKey: ['company-stats', 'scores', orgId, filters],
        queryFn: async () => {
            const res = await generationScoreStatsApiV1StatsOrgIdScoresGet({
                path: { org_id: orgId! },
                query: { brand: filters?.brand ?? null, seller: filters?.seller ?? null },
            })
            return res.data as GenerationScoreStatsApiV1StatsOrgIdScoresGetResponse
        },
        retry: false,
        enabled: enabled && Boolean(orgId),
        refetchOnWindowFocus: false,
    })

export const useCompanyStatsSegmentScores = (
    orgId: string | undefined,
    filters?: CompanyStatsQueryFilters,
    enabled = true
) =>
    useQuery<GenerationSegmentScoreStatsApiV1StatsOrgIdSegmentScoresGetResponse, AxiosError>({
        queryKey: ['company-stats', 'segment-scores', orgId, filters],
        queryFn: async () => {
            const res = await generationSegmentScoreStatsApiV1StatsOrgIdSegmentScoresGet({
                path: { org_id: orgId! },
                query: { brand: filters?.brand ?? null, seller: filters?.seller ?? null },
            })
            return res.data as GenerationSegmentScoreStatsApiV1StatsOrgIdSegmentScoresGetResponse
        },
        retry: false,
        enabled: enabled && Boolean(orgId),
        refetchOnWindowFocus: false,
    })

export const useCompanyStatsProducts = (
    orgId: string | undefined,
    filters?: CompanyStatsQueryFilters,
    enabled = true
) =>
    useQuery<GetTopBottomProductsApiV1StatsOrgIdProductsGetResponse, AxiosError>({
        queryKey: ['company-stats', 'products', orgId, filters],
        queryFn: async () => {
            const res = await getTopBottomProductsApiV1StatsOrgIdProductsGet({
                path: { org_id: orgId! },
                query: { brand: filters?.brand ?? null, seller: filters?.seller ?? null },
            })
            return res.data as GetTopBottomProductsApiV1StatsOrgIdProductsGetResponse
        },
        retry: false,
        enabled: enabled && Boolean(orgId),
        refetchOnWindowFocus: false,
    })

export const useCompanyStatsProductsScores = (
    orgId: string | undefined,
    params: CompanyStatsProductsScoresQueryParams,
    enabled = true
) =>
    useQuery<GetProductsScoresApiV1StatsOrgIdProductsScoresGetResponse, AxiosError>({
        queryKey: ['company-stats', 'products-scores', orgId, params],
        queryFn: async () => {
            const res = await getProductsScoresApiV1StatsOrgIdProductsScoresGet({
                path: { org_id: orgId! },
                query: {
                    page: params.page,
                    size: params.size,
                    order_by: params.order_by,
                    direction: params.direction,
                    target: params.target ?? null,
                    brand: params.brand ?? null,
                    seller: params.seller ?? null,
                },
            })
            return res.data as GetProductsScoresApiV1StatsOrgIdProductsScoresGetResponse
        },
        retry: false,
        enabled: enabled && Boolean(orgId),
        refetchOnWindowFocus: false,
    })

export { useCompanyStatsBrandComparison } from './useCompanyStatsBrandComparison'
export type { BrandComparisonBrandData, UseCompanyStatsBrandComparisonResult } from './useCompanyStatsBrandComparison'
export { useCompanyStatsDetailedBrandComparison } from './useCompanyStatsDetailedBrandComparison'
export type { UseCompanyStatsDetailedBrandComparisonResult } from './useCompanyStatsDetailedBrandComparison'
